import React, { useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';
import { I18NConsumer, I18N, I18NContext, GlobalContext, EBPagination } from 'commons';
import { baseUrl, orgId, Ajax, setPageTitle, getUrlParams } from 'utils';
import { Link } from '@reach/router';
import SocialSharing from '../../commons/SocialSharing';
import SignUpWidget from './signUpWidget';

const useEvents = (organizationId, isLogin) => {
  const url = `/nixle/eventSubscription/${organizationId}/list`;
  const [isLoading, updateIsLoading] = useState(true);
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);

  const request = () => {
    const reqData = new FormData();
    reqData.append('isLogin', isLogin);
    Ajax.post(url, reqData).then(({ data }) => {
      setEvents(data);
      updateIsLoading(false);
    }).catch(err => {
      setError(err);
      updateIsLoading(false);
    });
  };

  useEffect(() => {
    updateIsLoading(true);
    request();
  }, [organizationId]);

  return [isLoading, events, error, request];
};

const useEvent = (organizationId, isLogin) => {
  const [isLoading, updateIsLoading] = useState(true);
  const [eventId, udpateEventId] = useState(null);
  const [event, setEvent] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [pageSize, setPageSize] = useState(0);
  const [currentTime, setCurrentTime] = useState(null);
  const [error, setError] = useState(null);

  const request = () => {
    const url = `/nixle/eventSubscription/${organizationId}/${eventId}`;
    const reqData = new FormData();
    reqData.append('isLogin', isLogin);
    Ajax.post(url, reqData).then(({ data }) => {
      const {
        result,
        success,
        message,
      } = data;
      if (success) {
        const {
          eventSubscription,
          notificationList,
          pageSize: _pageSize,
          currentTime: _currentTime,
        } = result;
        setEvent(eventSubscription);
        setNotifications(notificationList);
        setPageSize(_pageSize);
        setCurrentTime(_currentTime);
      } else {
        setError(message);
      }
      updateIsLoading(false);
    }).catch(err => {
      setError(err);
      updateIsLoading(false);
    });
  };

  useEffect(() => {
    if (eventId) {
      updateIsLoading(true);
      request();
    }
  }, [organizationId, eventId]);

  return [isLoading, event, notifications, pageSize, currentTime, error, udpateEventId];
};

const usePagination = (organizationId, isLogin) => {
  const [isLoading, updateIsLoading] = useState(true);
  const [eventId, udpateEventId] = useState(null);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [pageTotal, setPageTotal] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [pageNo, setPageNo] = useState(1)

  const request = (page, pageSize) => {
    const url = `/nixle/eventSubscription/${organizationId}/${eventId}/page`;

    // const reqData = new FormData();
    // reqData.append('isLogin', isLogin);
    Ajax.get(url, {
      params: {
        pageNo: page,
        pageSize
      }
    }).then(({ data }) => {
      setNotifications(data.data);
      setPageTotal(data.count)
      updateIsLoading(false);
    }).catch(err => {
      setError(err);
      updateIsLoading(false);
    });
  };

  const paginationChange = (page, pageSize) => {
    if (page && pageSize) {
      updateIsLoading(true);
      if (eventId) {
        request(page, pageSize);
      }
      setPageNo(page)
      setPageSize(pageSize)
    }
  }

  useEffect(() => {
    // console.log('fetch more notifications: ', organizationId, eventId, baseTime);
    if (organizationId && eventId) {
      updateIsLoading(true);
      paginationChange(pageNo, pageSize);
    }
  }, [organizationId, eventId]);

  return [isLoading, notifications, udpateEventId, paginationChange, pageTotal, pageSize, pageNo, setPageNo, setPageSize]
}

const timePeriodsFormat = (startTime, endTime, tz) => {
  const timeZone = tz || 0;

  if (!startTime || !endTime) {
    return '';
  }

  const start = moment.tz(startTime, timeZone);
  const end = moment.tz(endTime, timeZone);

  if (start.isSame(end, 'day')) {
    return `${start.format('LLLL')} - ${end.format('LT z')}`;
  }

  return `${start.format('LLLL')} -
          ${end.format('LLLL z')}`;
};

export default function EventList(props) {
  const { orgName, isLoggedIn, location } = props;

  const { lang } = useContext(GlobalContext);


  const i18n = useContext(I18NContext);

  const query = getUrlParams(window.location.search);
  const eventIdInURL = (query) ? query.eventId : '';

  const [currentEventId, updateCurrentEventId] = useState(eventIdInURL);
  const [isLoadingEvents, events, eventsError, refreshEvents] = useEvents(orgId, isLoggedIn);
  const [isLoadingEvent, event, notifications,
    oldPageSize, currentTimeInResponse, eventError, setEventId] = useEvent(orgId, isLoggedIn);
  const [eventErrorMessage, setEventErrorMessage] = useState(null);
  const [baseTime, setBaseTime] = useState(1);
  const [notificationPool, updateNotificationPool] = useState([]);
  const [isLoadingNotifications, moreNotifications, udpateEventId, updatePagination, pageTotal, pageSize, pageNo, setPageNo, setPageSize] = usePagination(orgId, isLoggedIn)

  useEffect(() => {
    let l = 'en';
    switch (lang.toLowerCase()) {
      case 'zh_tw':
        l = 'zh-tw';
        break;
      case 'no_no':
        l = 'nb';
        break;
      case 'ht_ht':
        l = 'en';
        break;
      default:
        l = lang.toLowerCase();
    }
    moment.locale(l);
  }, [lang]);

  useEffect(() => {
    // console.log('execut effect by component mounted:', currentEventId);
    if (!isLoadingEvents && events.length > 0) {
      const defaultEventId = eventIdInURL || events[0].id;
      // console.log('default eventId: ', defaultEventId);
      updateCurrentEventId(defaultEventId);
    }
  }, [isLoadingEvents]);

  useEffect(() => {
    // console.log('execut effect by currentEventId change:', currentEventId);
    updateNotificationPool([]);
    setEventId(currentEventId);
    udpateEventId(currentEventId);

    if (location && location.state && location.state.page && location.state.pageSize) {
      // console.log('location: ', location.state.page, location.state.pageSize)
      updatePagination(location.state.page, location.state.pageSize)

      const { state } = history
      delete state.page
      delete state.pageSize
      history.replaceState(state, '')
    } else {
      // use default setting for first loading & event switching
      updatePagination()
    }
  }, [currentEventId]);

  useEffect(() => {
    // when pagination response (isLoadingNotifications) faster
    // wait for initiation response (event)
    if (event && !isLoadingNotifications && moreNotifications && moreNotifications.length > 0) {
      updateNotificationPool([...moreNotifications]);
    }
  }, [isLoadingNotifications, event]);

  useEffect(() => {
    if (eventError) {
      // console.log('event error:', eventError);
      setEventErrorMessage(eventError);
    }
  }, [eventError]);

  const onSelectedEventChange = (e) => {
    const { currentTarget: { value } } = e;
    updateCurrentEventId(value);
  };

  const getPeriodText = (startTime, endTime, tz) => {
    const sub = endTime - startTime;
    const timeZone = tz || 0;

    if (Number.isNaN(sub) || sub < 0) {
      return '';
    }

    if (sub < 1000 * 60 * 60) {
      return `${moment(sub).format('m')} ${i18n.get('eventSubscription.notification.label.minute')}`;
    }

    if (sub < 1000 * 60 * 60 * 24) {
      return `${moment.tz(sub, 0).format('H')} 
        ${i18n.get('eventSubscription.notification.label.hour')}`;
    }

    const start = moment.tz(startTime, timeZone);
    const end = moment.tz(endTime, timeZone);

    if (start.isSame(end, 'month')) {
      return `${moment.tz(sub, 0).format('D')} ${i18n.get('eventSubscription.notification.label.day')}`;
    }

    if (start.isSame(end, 'year')) {
      return start.format('MMM D');
    }

    return start.format('LLLL z');
  };

  const isExpired = (start, end, expireTime) => {
    const sub = end - start;
    const expire = parseInt(expireTime, 10);
    return sub && !Number.isNaN(sub) && expireTime &&
      !Number.isNaN(expire) && sub > 1000 * 60 * 60 * expire;
  };

  return (
    <div className="well ssp-well container col-11">
      <I18NConsumer>
        {(i18n) => setPageTitle(`${orgName} - ${i18n['eventSubscription.title.events']}`)}
      </I18NConsumer>
      <div className="row">
        {
          eventErrorMessage &&
          <div className="col-md-8">
            <h2>
              <small>{eventErrorMessage}</small>
            </h2>
          </div>
        }
        {
          !eventErrorMessage &&
          <>
            <div className="col-md-8">
              <h1 className="h1"><I18N>eventSubscription.title.events</I18N></h1>
              <p className="text-muted"><I18N>eventSubscription.list.summary</I18N></p>
              <div className="mt-3">
                <label htmlFor="eventsSelect" className="mr-2"><I18N>eventSubscription.list.label</I18N></label>
                <select
                  className="form-control labeled-select"
                  name="events"
                  value={currentEventId}
                  onChange={onSelectedEventChange}
                  eb-test="eventSubscription.event.selector"
                  aria-label={i18n.get('eventSubscription.title.events')}
                  id="eventsSelect"
                >
                  {
                    (events) ? events.map(item => (
                      <option value={item.id} key={item.id}>
                        {item.groupName}
                        {item.isPrivate && ` (${i18n.get('eventSubscription.field.private')})`}
                      </option>
                    )) : null
                  }
                </select>
              </div>
              <h2 className="h2 margin-top-2" eb-test="eventSubscription.event.name">{(isLoadingEvent || isLoadingEvents) ? '' : event.groupName}</h2>
              <div className="h5" id="event_period">
                {
                  (isLoadingEvent || isLoadingEvents) ? '' :
                    timePeriodsFormat(event.eventSubscriptionWebPage.eventStartDate,
                      event.eventSubscriptionWebPage.eventEndDate,
                      event.eventSubscriptionWebPage.timeZone)
                }
              </div>
            </div>
          </>
        }
      </div>
      {
        event && (event.eventSubscriptionWebPage.eventAddress ||
          event.eventSubscriptionWebPage.socialSharing) &&
        <div className="row box-pd5 gray-bg">
          <div className="col-md-8">
            {
              event.eventSubscriptionWebPage.eventAddress &&
              <div className="text-break p-1" id="event_address">
                <I18N>eventSubscription.field.location</I18N>:
                {` ${event.eventSubscriptionWebPage.eventAddress}`}
              </div>
            }
          </div>
          {
            event.eventSubscriptionWebPage.socialSharing &&
            <div className="col-md-4 text-right">
              {
                !isLoadingEvent &&
                  <SocialSharing
                    url={`${baseUrl}/${orgId}/event?eventId=${event.id}`}
                    title={event.groupName}
                    description={event.groupName}
                    alignment="right"
                  />
              }
            </div>
          }
        </div>
      }
      <div className="row">
        {
          !eventErrorMessage &&
          <div className="col-md-8">
            <div>
              {
                notificationPool.map((notificationObj) => (
                  <div className="notification-box" key={notificationObj.notificationId} role="document">
                    <div className="row notification-title mx-0">
                      <div className="col-lg-2 p-0 pull-left alert-level position-relative">
                        <label
                          className={
                            classnames('text-center color-box', {
                              'btn-success': notificationObj.type !== 2 &&
                                notificationObj.type !== 3,
                              'btn-danger': notificationObj.type === 2,
                              'btn-warning': notificationObj.type === 3
                            })
                          }
                          id={`event_level_label_${notificationObj.notificationId}`}
                        >
                          {
                            notificationObj.type !== 2 && notificationObj.type !== 3 &&
                            <span id="notification-field-type-community" className="notification-label">
                              <I18N>eventSubscription.notification.field.type.community</I18N>
                            </span>
                          }
                          {
                            notificationObj.type === 2 &&
                            <span id="notification-field-type-alert">
                              <I18N>eventSubscription.notification.field.type.alert</I18N>
                            </span>
                          }
                          {
                            notificationObj.type === 3 &&
                            <span id="notification-field-type-advisory" className="notification-label">
                              <I18N>eventSubscription.notification.field.type.advisory</I18N>
                            </span>
                          }
                        </label>
                      </div>
                      <div className="col-10">
                        <h2
                          className="eventtile"
                        >
                          <span id={`notification_title_${notificationObj.notificationId}`}>
                            <Link
                              to={`${notificationObj.notificationId}?event_id=${event.id}`}
                              state={{ page: pageNo, pageSize }}
                              title={`${notificationObj.title}`}
                              eb-test="eventSubscription.event.seeMore.link"
                              aria-label={`${notificationObj.title}`}
                              aria-describedby={`notification-field-type-${notificationObj.type === 3 ? 'advisory' : notificationObj.type === 2 ? 'alert' : 'community'}`}
                            >
                              {notificationObj.title}
                            </Link>
                          </span>
                          {
                            notificationObj.attachmentFiles.length > 0 &&
                            <Link
                              to={`${notificationObj.notificationId}?event_id=${event.id}`}
                              state={{ page: pageNo, pageSize }}
                              eb-test="eventSubscription.event.attachment.link"
                              aria-label={`${notificationObj.attachmentFiles.length} ${i18n.get('eventSubscription.notification.field.attachment')}`}
                            >
                              <i className="attachment-icon fas fa-paperclip" />
                            </Link>
                          }
                        </h2>
                        <div className="row sub-title" id={`notification_date_${notificationObj.notificationId}`}>
                          <small>
                            {getPeriodText(notificationObj.notificationSendDate,
                              currentTimeInResponse, notificationObj.timeZone)}
                          </small>
                          {
                            isExpired(notificationObj.notificationSendDate,
                              currentTimeInResponse, notificationObj.expire) &&
                            <small className="text-danger">
                              <I18N>eventSubscription.notification.message.expired</I18N>
                            </small>
                          }
                        </div>
                      </div>
                    </div>
                    <p className="notification-message">
                      <span id={`notification_content_${notificationObj.notificationId}`}>
                        {notificationObj.textMessage}
                      </span>
                    </p>
                  </div>
                ))
              }
            </div>
            {
              notificationPool.length > 0 &&
              <div className="text-center">
                <EBPagination
                  showTotal={(total, range) => <I18N>{`event.table.pagination.pageInfo,${range[0]},${range[1]}, ${total}`}</I18N>}
                  defaultCurrent={1}
                  total={pageTotal}
                  onChange={(page, pageSize) => updatePagination(page, pageSize)}
                  pageSize={pageSize}
                  current={pageNo}
                />
              </div>
            }
            {
              notificationPool.length === 0 &&
              <div className="text-center padding20" ng-if="pages.length === 0">
                <h2><I18N>notification.list.empty</I18N></h2>
                {
                  !isLoggedIn &&
                  <p><I18N>eventSubscription.message.signUpNow</I18N></p>
                }
              </div>
            }
          </div>
        }
        {
          event &&
          (event.eventSubscriptionWebPage.requireName ||
            event.eventSubscriptionWebPage.requireMobile ||
            event.eventSubscriptionWebPage.requireEmail) &&
          <div className="col-md-4 signup-box">
            <SignUpWidget eventSubscription={event} />
          </div>
        }
      </div>
    </div>
  );
}

EventList.propTypes = {
  orgName: PropTypes.string.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
};
